import React, { memo, useEffect, useRef, useState } from 'react'

import { CSSTransition } from 'react-transition-group'
import { motion, useAnimation } from 'framer-motion'

import cn from 'classnames'
import styles from './styles.module.scss'
import useMediaQuery from '@mui/material/useMediaQuery'

import { ReactComponent as TrashIcon } from '../../../assets/icons/trashIcon.svg'
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg'
import { ReactComponent as CloseIcon } from '../../../assets/icons/close-white.svg'
import { ReactComponent as CalcOfFilesIcon } from '../../../assets/icons/calcOfFiles.svg'
import { ReactComponent as DeleteFile } from '../../../assets/icons/close.svg'

import ImageComponent from '../../../UI/Image/ImageComponent'
import PhotoGalleryModal from '../../../UI/PhotoGalleryModal/PhotoGalleryModal'

const EducationGroupItem = memo(({ type, data, onDelete, onEdit, deleteFile, isActive, isSwiped, onSwipeChange }) => {
  const [visibleListOfFiles, setVisibleListOfFiles] = useState(false)
  const [uncontrolledIsSwiped, setUncontrolledIsSwiped] = useState(false)
  const [isGalleryOpen, setIsGalleryOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const isTabletOrMobile = useMediaQuery('(max-width: 1200px)')
  const isSwipedValue = typeof isSwiped === 'boolean' ? isSwiped : uncontrolledIsSwiped

  const swipeRef = useRef({
    startX: 0,
    startY: 0,
    pointerId: null,
    isTracking: false,
    isHorizontal: false,
  })

  const nodeRef = useRef(null)
  const control = useAnimation()

  useEffect(() => {
    if (visibleListOfFiles) {
      control.start((i) => ({
        opacity: 1,
        x: 0,
        transition: { delay: i * 0.1, type: 'spring' },
      }))
    } else {
      control.start((i) => ({
        opacity: 0,
        x: 0,
        transition: { delay: i * 0.1, type: 'spring' },
      }))
    }

    // eslint-disable-next-line
  }, [visibleListOfFiles])

  const handleFilesEnter = () => {
    setVisibleListOfFiles(true)
  }
  const handleFilesLeave = () => {
    setVisibleListOfFiles(false)
  }

  const openGallery = (index = 0) => {
    setActiveIndex(index)
    setIsGalleryOpen(true)
  }

  const closeGallery = () => {
    setIsGalleryOpen(false)
  }

  const isInteractiveTarget = (target) => {
    if (!target?.closest) return false
    return Boolean(target.closest('button, a, input, label, textarea, select'))
  }

  const handlePointerDown = (e) => {
    if (isInteractiveTarget(e.target)) return

    swipeRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      pointerId: e.pointerId,
      isTracking: true,
      isHorizontal: false,
    }

    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch (_) {
      // ignore
    }
  }

  const handlePointerMove = (e) => {
    const st = swipeRef.current
    if (!st.isTracking || st.pointerId !== e.pointerId) return

    const dx = e.clientX - st.startX
    const dy = e.clientY - st.startY

    if (!st.isHorizontal) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return
      st.isHorizontal = Math.abs(dx) > Math.abs(dy)
    }

    if (st.isHorizontal) {
      e.preventDefault()
    }
  }

  const handlePointerEnd = (e) => {
    const st = swipeRef.current
    if (!st.isTracking || st.pointerId !== e.pointerId) return

    const dx = e.clientX - st.startX
    const dy = e.clientY - st.startY

    if (Math.abs(dx) > Math.abs(dy)) {
      if (dx < -40) {
        if (typeof onSwipeChange === 'function') onSwipeChange(true)
        else setUncontrolledIsSwiped(true)
      }
      if (dx > 30) {
        if (typeof onSwipeChange === 'function') onSwipeChange(false)
        else setUncontrolledIsSwiped(false)
      }
    }

    swipeRef.current.isTracking = false
    swipeRef.current.pointerId = null
  }

  return (
    <div
      className={cn(styles.card, { [styles.card__active]: isActive, [styles.swiped]: isSwipedValue })}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
    >
      <div className={styles.cardInfo}>
        <div className={styles.documentType}>{data.documentName}</div>

        <div className={styles.documentBody}>
          <div className={styles.documentBody_info}>
            <div className={styles.degree}>
              Degree: <p className={styles.degreeValue}>{data.degree}</p>
            </div>

            <div className={styles.period}>
              Period: <p className={styles.periodValue}>{data.period}</p>
            </div>
          </div>

          <div className={styles.files}>
            {data?.files?.length ? (
              <div
                key={data.files[0].id}
                className={styles.file}
                onMouseEnter={!isTabletOrMobile ? handleFilesEnter : undefined}
              >
                <div key={data.files[0].id} className={styles.file}>
                  <div className={styles.calcOfFiles}>
                    <label className={styles.calcOfFilesLabel}>{data.files.length}</label>

                    <CalcOfFilesIcon />
                  </div>

                  <a
                    rel='noopener noreferrer'
                    target='_blank'
                    href={data.files[0].file}
                    onClick={(e) => {
                      if (!isTabletOrMobile) return
                      e.preventDefault()
                      openGallery(0)
                    }}
                  >
                    <ImageComponent src={data.files[0].file_mini} alt='file' />
                  </a>
                </div>
              </div>
            ) : (
              <div className={styles.file} />
            )}
          </div>

          {!isTabletOrMobile && (
            <CSSTransition
              nodeRef={nodeRef}
              in={visibleListOfFiles}
              timeout={500}
              unmountOnExit
              classNames='listOfFiles'
            >
              <div ref={nodeRef} className='listOfFiles' onMouseLeave={handleFilesLeave}>
                <div className={styles.files_list}>
                  {data?.files?.length &&
                    data.files.map((file) => (
                      <motion.div
                        key={file.id}
                        className={styles.file}
                        initial={{ opacity: 0, x: 20 }}
                        animate={control}
                        custom={file.index}
                      >
                        <div key={file.id} className={styles.file}>
                          <div
                            className={styles.deleteFile}
                            onClick={() => deleteFile(file.id, data.id, type, isActive)}
                          >
                            <DeleteFile />
                          </div>

                          <a rel='noopener noreferrer' target='_blank' href={file.file}>
                            <ImageComponent src={file.file_mini} alt='file' />
                          </a>
                        </div>
                      </motion.div>
                    ))}
                </div>
              </div>
            </CSSTransition>
          )}
        </div>
      </div>

      <PhotoGalleryModal
        open={Boolean(isGalleryOpen && isTabletOrMobile)}
        title={data.documentName}
        files={data?.files || []}
        initialIndex={activeIndex}
        onClose={closeGallery}
        onRequestDelete={(file) => deleteFile(file.id, data.id, type, isActive)}
      />

      {data.id !== 'default' && (
        <div className={styles.cardControls}>
          <button className={cn(styles.cardControl, styles.cardControlEdit)} onClick={() => onEdit(data.id, !isActive)}>
            {isActive ? <CloseIcon /> : <EditIcon />}
          </button>

          <button
            className={cn(styles.cardControl, styles.cardControlDelete)}
            onClick={() => onDelete(data.id, isActive)}
          >
            <TrashIcon />
          </button>
        </div>
      )}
    </div>
  )
})

export default EducationGroupItem
